import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight } from "lucide-react";
import { useSEO } from "@/hooks/useSEO";
import { PageHero } from "@/components/shared/PageHero";
import { Stagger, Item } from "@/components/shared/Reveal";
import { ClosingCTA } from "@/components/home/ClosingCTA";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Insights() {
  useSEO({ title: "Insights | Transportation Technology Perspectives | Wexoz", description: "Perspectives from Wexoz on fleet IoT, logistics automation, school transport technology, video telematics, AI and systems integration." });
  const [items, setItems] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    axios.get(`${API}/insights`).then((r) => setItems(r.data)).catch(() => setError(true));
  }, []);

  return (
    <>
      <PageHero eyebrow="Insights" lines={["Perspectives on", "transportation technology."]} text="Practical thinking on fleet IoT, logistics automation, school transport, video intelligence, AI and integration — written for the people who run operations." />
      <section className="section">
        <div className="wrap">
          {error && <p data-testid="insights-error" className="text-slate-400">Insights are temporarily unavailable.</p>}
          {!items && !error && <div data-testid="insights-loading" className="grid gap-6 md:grid-cols-2">{[0, 1].map((i) => <div key={i} className="h-80 animate-pulse rounded-2xl bg-navy-800" />)}</div>}
          {items && (
            <Stagger className="grid gap-6 md:grid-cols-2">
              {items.map((a, i) => (
                <Item key={a.slug} className={i === 0 ? "md:col-span-2" : ""}>
                  <Link to={`/insights/${a.slug}`} data-testid={`insight-card-${a.slug}`} className={`card group flex overflow-hidden ${i === 0 ? "flex-col lg:flex-row" : "flex-col"}`}>
                    <div className={`relative overflow-hidden ${i === 0 ? "aspect-[16/9] lg:aspect-auto lg:w-1/2" : "aspect-[16/9]"}`}>
                      <img src={a.cover} alt="" className="h-full w-full object-cover opacity-70 transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                      <div className="absolute inset-0 bg-gradient-to-t from-navy-800 to-transparent" />
                    </div>
                    <div className="flex flex-1 flex-col p-8">
                      <div className="flex items-center gap-3 font-mono text-[11px] uppercase tracking-wider text-slate-500">
                        <span className="text-cyan-400">{a.category}</span><span>·</span><span>{a.read_time}</span><span>·</span><span>{a.published_at}</span>
                      </div>
                      <h2 className={`mt-4 font-semibold leading-tight ${i === 0 ? "text-3xl" : "text-2xl"}`}>{a.title}</h2>
                      <p className="mt-4 text-sm leading-relaxed text-slate-400">{a.excerpt}</p>
                      <span className="mt-auto inline-flex items-center gap-1 pt-6 text-sm font-semibold text-cyan-400">Read article <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></span>
                    </div>
                  </Link>
                </Item>
              ))}
            </Stagger>
          )}
        </div>
      </section>
      <ClosingCTA />
    </>
  );
}
